import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useTransition,
  KeyboardEvent,
} from "react";
import ReactFlow, {
  Controls,
  Background,
  ReactFlowInstance,
  Node,
} from "reactflow";
import {
  Alert,
  Button,
  LoadingOverlay,
  Menu,
  Text,
  Box,
  List,
  Loader,
  Tooltip,
  Flex,
  useMantineColorScheme,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { useContextMenu } from "mantine-contextmenu";
import {
  IconSettings,
  IconTextPlus,
  IconTerminal,
  IconSettingsAutomation,
  IconFileSymlink,
  IconRobot,
  IconRuler2,
  IconArrowMerge,
  IconArrowsSplit,
  IconForms,
  IconAbacus,
  IconDeviceFloppy,
  IconHeart,
  IconCheckbox,
  IconTransform,
  IconSortAscending,
  IconChevronDown,
  IconFileCode,
  IconFileDownload,
  IconAlertTriangle,
} from "@tabler/icons-react";
import RemoveEdge from "./RemoveEdge";
import TextFieldsNode from "./TextFieldsNode"; // Import a custom node
import PromptNode from "./PromptNode";
import CodeEvaluatorNode from "./CodeEvaluatorNode";
import VisNode from "./VisNode";
import InspectNode from "./InspectorNode";
import SelectVarsNode from "./SelectVarsNode";
import ScriptNode from "./ScriptNode";
import { AlertModalContext } from "./AlertModal";
import ItemsNode from "./ItemsNode";
import TabularDataNode from "./TabularDataNode";
import JoinNode from "./JoinNode";
import SplitNode from "./SplitNode";
import CommentNode from "./CommentNode";
import MultiEvalNode from "./MultiEvalNode";
import RerankNode from "./RerankNode";
import RagChatNode from "./RagChatNode";
import GlobalSettingsModal, {
  GlobalSettingsModalRef,
} from "./GlobalSettingsModal";
import ExampleFlowsModal, { ExampleFlowsModalRef } from "./ExampleFlowsModal";
import AreYouSureModal, { AreYouSureModalRef } from "./AreYouSureModal";
import LLMEvaluatorNode from "./LLMEvalNode";
import SimpleEvalNode from "./SimpleEvalNode";
import UploadNode from "./UploadNode";
import ChunkNode from "./ChunkNode";
import RetrievalNode from "./RetrievalNode";
import {
  ModelSettings,
  getDefaultModelFormData,
  getDefaultModelSettings,
  postProcessFormData,
} from "./ModelSettingSchemas";
import { NativeLLM, OPENROUTER_PREFIX } from "./backend/models";
import { v4 as uuid } from "uuid";
import axios from "axios";
import LZString from "lz-string";
import { EXAMPLEFLOW_1 } from "./example_flows";
import MediaNode from "./MediaNode";

// Styling
import "reactflow/dist/style.css"; // reactflow
import "./styles.css"; // ChainForge CSS styling

// State management (from https://reactflow.dev/docs/guides/state-management/)
import { shallow } from "zustand/shallow";
import useStore, { StoreHandles } from "./store";
import StorageCache, { MediaLookup, StringLookup } from "./backend/cache";
import { FlowLoadSource, flowLoadReplacesMedia } from "./backend/flowLoading";
import { claimActiveTab, isActiveTab } from "./backend/activeTab";
import { FlowLoadGuard } from "./backend/flowLoadGuard";
import {
  StorageProtection,
  checkStorageProtection,
  dismissStorageNotice,
  getStorageProtection,
  requestStorageProtection,
  storageNoticeDismissed,
  subscribeStorageProtection,
} from "./backend/storagePersistence";
import {
  APP_IS_RUNNING_LOCALLY,
  browserTabIsActive,
  FLASK_BASE_URL,
} from "./backend/utils";
import { Dict, JSONCompatible, LLMSpec } from "./backend/typing";
import {
  ensureUniqueFlowFilename,
  exportCache,
  exportFlowBundle,
  fetchEnvironAPIKeys,
  fetchExampleFlow,
  fetchOpenAIEval,
  importCache,
  importFlowBundle,
  saveFlowToLocalFilesystem,
} from "./backend/backend";

// Device / Browser detection
import {
  isMobile,
  isTablet,
  isChrome,
  isFirefox,
  isEdgeChromium,
  isChromium,
  isMobileSafari,
  isSafari,
} from "react-device-detect";
import FlowSidebar from "./FlowSidebar";
import FlowNameEditor from "./FlowNameEditor";
import NestedMenu, { NestedMenuItemProps } from "./NestedMenu";
import { ragNodeAvailable } from "./backend/ragCapabilities";
import {
  serverStopsWhenIdle,
  startServerHeartbeat,
} from "./backend/serverHeartbeat";
import RequestClarificationModal, {
  RequestClarificationModalProps,
} from "./RequestClarificationModal";
import { jsontoYml } from "./backend/jsonToYml";

const IS_ACCEPTED_BROWSER =
  (isChrome ||
    isChromium ||
    isEdgeChromium ||
    isFirefox ||
    isSafari ||
    (navigator as any)?.brave !== undefined) &&
  (!isMobile || (isTablet && !isMobileSafari));

// Whether we are running on localhost or not, and hence whether
// we have access to the Flask backend for, e.g., Python code evaluation.
const IS_RUNNING_LOCALLY = APP_IS_RUNNING_LOCALLY();

const SAVE_FLOW_FILENAME_TO_BROWSER_CACHE = (name: string) => {
  console.log("Saving flow filename", name);
  // Save the current filename of the user's working flow
  StorageCache.saveToLocalStorage("chainforge-cur-file", {
    flowFileName: name,
  });
};

// Get center of ReactFlow viewport
const getViewportCenter = (rfInstance: ReactFlowInstance | null) => {
  const { centerX, centerY } = getWindowCenter();
  if (rfInstance === null) return { x: centerX, y: centerY };
  // Support Zoom
  const { x, y, zoom } = rfInstance.getViewport();
  return { x: -(x / zoom) + centerX / zoom, y: -(y / zoom) + centerY / zoom };
};

const selector = (state: StoreHandles) => ({
  nodes: state.nodes,
  edges: state.edges,
  onNodesChange: state.onNodesChange,
  onEdgesChange: state.onEdgesChange,
  onConnect: state.onConnect,
  addNode: state.addNode,
  setNodes: state.setNodes,
  setEdges: state.setEdges,
  resetLLMColors: state.resetLLMColors,
  setAPIKeys: state.setAPIKeys,
  importState: state.importState,
  favorites: state.favorites,
  removeFavorite: state.removeFavorite,
});

// The initial LLM to use when new flows are created, or upon first load
const INITIAL_LLM = () => {
  const qwenWebLLM = {
    key: uuid(),
    name: "Qwen2.5 0.5B",
    emoji: "🌐",
    model: NativeLLM.WebLLM_Qwen2_5_0_5B,
    base_model: "webllm",
    temp: 0.7,
    settings: getDefaultModelSettings("webllm"),
    formData: getDefaultModelFormData("webllm"),
  } satisfies LLMSpec;
  qwenWebLLM.formData.shortname = qwenWebLLM.name;
  qwenWebLLM.formData.model = qwenWebLLM.model;
  return qwenWebLLM;
};

/**
 * The OpenAI evals were converted to flows in 2023, all with GPT-3.5, which
 * no longer exists. Swap in a current, cheap model through OpenRouter as each
 * one loads, keeping the eval's own system message.
 */
const withCurrentOpenAIEvalModel = (flowJSON: Dict): Dict => {
  const model = "openai/gpt-5.4-mini";
  for (const node of flowJSON?.flow?.nodes ?? []) {
    const llms: LLMSpec[] | undefined = node.data?.llms;
    if (!Array.isArray(llms)) continue;
    node.data.llms = llms.map((old) => {
      const formData = getDefaultModelFormData("openrouter", model);
      formData.shortname = "GPT-5.4 Mini";
      // These evals grade short, formatted answers, so skip reasoning.
      formData.reasoning_effort = "off";
      if (typeof old.settings?.system_msg === "string")
        formData.system_msg = old.settings.system_msg;
      return {
        key: uuid(),
        name: "GPT-5.4 Mini",
        emoji: "🔀",
        model: OPENROUTER_PREFIX + model,
        base_model: "openrouter",
        temp: 1.0,
        formData,
        settings: postProcessFormData(ModelSettings.openrouter, formData),
      } satisfies LLMSpec;
    });
  }
  return flowJSON;
};

const nodeTypes = {
  textfields: TextFieldsNode, // Register the custom node
  prompt: PromptNode,
  selectvars: SelectVarsNode,
  chat: PromptNode,
  simpleval: SimpleEvalNode,
  evaluator: CodeEvaluatorNode,
  llmeval: LLMEvaluatorNode,
  multieval: MultiEvalNode,
  vis: VisNode,
  inspect: InspectNode,
  script: ScriptNode,
  csv: ItemsNode,
  table: TabularDataNode,
  comment: CommentNode,
  join: JoinNode,
  split: SplitNode,
  processor: CodeEvaluatorNode,
  upload: UploadNode,
  chunk: ChunkNode,
  retrieval: RetrievalNode,
  rerank: RerankNode,
  ragchat: RagChatNode,
  media: MediaNode,
};

const nodeEmojis = {
  textfields: <IconTextPlus size={16} />,
  prompt: "💬",
  chat: "🗣",
  simpleval: <IconRuler2 size={16} />,
  evaluator: <IconTerminal size={16} />,
  llmeval: <IconRobot size={16} />,
  multieval: <IconAbacus size={16} />,
  vis: "📊",
  inspect: "🔍",
  script: <IconSettingsAutomation size={16} />,
  csv: <IconForms size={16} />,
  table: "🗂️",
  comment: "✏️",
  join: <IconArrowMerge size={16} />,
  split: <IconArrowsSplit size={16} />,
  upload: "📂",
  chunk: "🧩",
  retrieval: "🎯",
  rerank: <IconSortAscending size={16} />,
  ragchat: "💭",
  media: "📺",
};

const edgeTypes = {
  default: RemoveEdge,
};

// Try to get a GET param in the URL, representing the shared flow.
// Returns undefined if not found.
const getSharedFlowURLParam = () => {
  // Get the current URL
  const curr_url = new URL(window.location.href);

  // Get the search parameters from the URL
  const params = new URLSearchParams(curr_url.search);

  // Try to retrieve an 'f' parameter (short for flow)
  const shared_flow_uid = params.get("f");

  if (shared_flow_uid) {
    // Check if it's a base36 string:
    const is_base36 = /^[0-9a-z]+$/i;
    if (shared_flow_uid.length > 1 && is_base36.test(shared_flow_uid))
      return shared_flow_uid;
  }
  return undefined;
};

// Fetches a shared flow (flow + cache data) by its uid from the hosted database.
const fetchSharedFlow = async (uid: string): Promise<Dict> => {
  const response = await fetch("/db/get_sharedflow.php", {
    method: "POST",
    body: uid,
  }).then((r) => r.text());
  if (!response || response.startsWith("Error"))
    throw new Error(response || "Unknown error");
  return JSON.parse(LZString.decompressFromUTF16(response));
};

const getWindowSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});
const getWindowCenter = () => {
  const { width, height } = getWindowSize();
  return { centerX: width / 2.0, centerY: height / 2.0 };
};

// const connectionLineStyle = { stroke: '#ddd' };
const snapGrid: [number, number] = [16, 16];

const App = () => {
  // Get nodes, edges, etc. state from the Zustand store:
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode: addNodeToStore,
    setNodes,
    setEdges,
    resetLLMColors,
    setAPIKeys,
    importState,
    favorites,
    removeFavorite,
  } = useStore(selector, shallow);

  // Color theme (dark or light mode)
  const { colorScheme } = useMantineColorScheme();

  // For saving / loading flows
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  const [autosavingInterval, setAutosavingInterval] = useState<
    NodeJS.Timeout | undefined
  >(undefined);

  // Saves are refused while a flow is loading, as the canvas is empty on the
  // way and saving it would replace the autosave. See ./backend/flowLoadGuard.
  const [flowLoad] = useState(() => new FlowLoadGuard());
  useEffect(() => {
    flowLoad.nodesRendered(nodes);
  }, [nodes]);

  // The 'name' of the current flow, to use when saving/loading
  const [flowFileName, setFlowFileName] = useState(`flow-${Date.now()}`);
  const safeSetFlowFileName = useCallback(async (newName: string) => {
    const uniqueName = await ensureUniqueFlowFilename(newName);
    setFlowFileName(uniqueName);
    SAVE_FLOW_FILENAME_TO_BROWSER_CACHE(uniqueName);
  }, []);
  const setFlowFileNameAndCache = useCallback((newName: string) => {
    setFlowFileName(newName);
    SAVE_FLOW_FILENAME_TO_BROWSER_CACHE(newName);
  }, []);

  // For 'share' button
  const clipboard = useClipboard({ timeout: 1500 });
  const [waitingForShare, setWaitingForShare] = useState(false);

  // Offload intensive computation to redraw and avoid blocking UI
  const [isSaving, startSaveTransition] = useTransition();
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  // Sticky, unlike showSaveSuccess: autosave runs every minute with alerts
  // suppressed, so a quota failure there would otherwise only reach the
  // console. This keeps the save button showing a problem until a save works.
  const [saveFailed, setSaveFailed] = useState(false);

  // For modal popup to set global settings like API keys
  const settingsModal = useRef<GlobalSettingsModalRef>(null);

  // For modal popup of example flows
  const examplesModal = useRef<ExampleFlowsModalRef>(null);

  // A flow from a shared link, fetched but waiting for the user to confirm it
  // may replace the flow they already have.
  const sharedFlowConfirmModal = useRef<AreYouSureModalRef>(null);
  const [pendingSharedFlow, setPendingSharedFlow] = useState<Dict | null>(null);

  // For an info pop-up that welcomes new users
  // const [welcomeModalOpened, { open: openWelcomeModal, close: closeWelcomeModal }] = useDisclosure(false);

  // For displaying alerts
  const showAlert = useContext(AlertModalContext);

  // For displaying a pending 'loading' status
  const [isLoading, setIsLoading] = useState(true);

  // Context menu for "Add Node +" list
  const { hideContextMenu } = useContextMenu();

  // Add a node to the flow
  const addNode = useCallback(
    (
      id: string,
      type?: string,
      data?: Dict,
      offsetX?: number,
      offsetY?: number,
    ) => {
      const { x, y } = getViewportCenter(rfInstance);
      addNodeToStore({
        id: `${id}-` + uuid(),
        type: type ?? id,
        data: data ?? {},
        position: {
          x: x - 200 + (offsetX || 0),
          y: y - 100 + (offsetY || 0),
        },
      });
    },
    [addNodeToStore, rfInstance],
  );

  // Add Nodes list
  const addNodesMenuItems = useMemo(() => {
    // RAG nodes are offered individually, by whether they can actually run:
    // some work client-side, others need the Flask backend with the `rag`
    // extra. See backend/ragCapabilities.
    const ragNodes = [
      {
        // Menu.Label
        key: "RAG",
      },
      {
        available: ragNodeAvailable("chunk"),
        key: "chunk",
        title: "Chunking Node",
        icon: nodeEmojis.chunk,
        tooltip:
          "Chunk texts into smaller pieces. Compare different chunking methods. Typically used after the Documents Node.",
        onClick: () => addNode("chunk"),
      },
      {
        available: ragNodeAvailable("retrieval"),
        key: "retrieval",
        title: "Retrieval Node",
        icon: nodeEmojis.retrieval,
        tooltip:
          "Given chunks and queries, retrieve relevant chunks for the given query. Compare retrieval methods across queries. Retrieval methods include both classical methods like BM25, and vector stores.",
        onClick: () => addNode("retrieval"),
      },
      {
        available: ragNodeAvailable("rerank"),
        key: "rerank",
        title: "Rerank Node",
        icon: nodeEmojis.rerank,
        tooltip: "Reranks retrieval outputs.",
        onClick: () => addNode("rerank"),
      },
      {
        available: ragNodeAvailable("ragchat"),
        key: "ragchat",
        title: "RAG Chat Node",
        icon: nodeEmojis.ragchat,
        tooltip:
          "Chat with a RAG pipeline. Connect its output to a Retrieval node's queries input: each message runs retrieval, reranking and your Prompt node, and shows the answers with the context behind them.",
        onClick: () => addNode("ragchat"),
      },
      {
        key: "divider",
      },
    ]
      // Drop the nodes that cannot run in the current setup, so nobody adds a
      // node that only fails when they press run.
      .filter((item) => (item as any).available !== false)
      .map(
        ({ available: _available, ...item }: any) => item,
      ) as NestedMenuItemProps[];

    // Misc nodes
    const miscNodes: NestedMenuItemProps[] = [
      {
        // Menu.Label
        key: "Misc",
      },
      {
        key: "comment",
        title: "Comment Node",
        icon: nodeEmojis.comment,
        tooltip: "Make a comment about your flow.",
        onClick: () => addNode("comment"),
      },
      {
        key: "script",
        title: "Global Python Scripts",
        icon: nodeEmojis.script,
        tooltip:
          "Specify directories to load as local packages, so they can be imported in your Python evaluator nodes (add to sys path).",
        onClick: () => addNode("scriptNode", "script"),
      },
      {
        key: "selectvars",
        title: "Filter Variables Node",
        icon: <IconCheckbox size={16} />,
        tooltip:
          "Filter which variables and metavariables to keep for the next steps.",
        onClick: () => addNode("selectVarsNode", "selectvars"),
      },
    ];

    // All initial nodes available in ChainForge
    let initNodes = [
      {
        // Menu.Label
        key: "Input Data",
      },
      {
        key: "textfields",
        title: "Text Fields Node",
        icon: nodeEmojis.textfields,
        tooltip:
          "Specify input text to prompt or chat nodes. You can also declare variables in brackets {} to chain TextFields together.",
        onClick: () => addNode("textFieldsNode", "textfields"),
      },
      {
        key: "table",
        title: "Tabular Data Node",
        icon: nodeEmojis.table,
        tooltip:
          "Import or create a spreadhseet of data to use as input to prompt or chat nodes. Import accepts xlsx, csv, and jsonl.",
        onClick: () => addNode("table"),
      },
      {
        key: "csv",
        title: "Items Node",
        icon: nodeEmojis.csv,
        tooltip:
          "Specify inputs as a comma-separated list of items. Good for specifying lots of short text values. An alternative to TextFields node.",
        onClick: () => addNode("csvNode", "csv"),
      },
      {
        key: "media",
        title: "Media Node",
        icon: nodeEmojis.media,
        tooltip: "Add image data with corresponding metadata.",
        onClick: () => addNode("media", "media"),
      },
      {
        // Documents feed more than RAG (e.g. a Processor extracting fields
        // from PDFs), so they sit with the other inputs. Uploading and reading
        // documents works client-side, so it's always offered.
        key: "upload",
        title: "Documents Node",
        icon: nodeEmojis.upload,
        tooltip:
          "Add documents to the flow, such as text files, PDFs or Word files. Each document's text becomes an input.",
        onClick: () => addNode("upload"),
      },
      {
        key: "divider",
      },
      {
        // Menu.Label
        key: "Prompters",
      },
      {
        key: "prompt",
        title: "Prompt Node",
        icon: nodeEmojis.prompt,
        tooltip:
          "Prompt one or multiple LLMs. Specify prompt variables in brackets {}.",
        onClick: () => addNode("promptNode", "prompt", { prompt: "" }),
      },
      {
        key: "chat",
        title: "Chat Turn Node",
        icon: nodeEmojis.chat,
        tooltip:
          "Start or continue a conversation with chat models. Attach Prompt Node output as past context to continue chatting past the first turn.",
        onClick: () => addNode("chatTurn", "chat", { prompt: "" }),
      },
      {
        key: "divider",
      },
      {
        // Menu.Label
        key: "Evaluators and Processors",
      },
      {
        key: "Evaluators",
        title: "Evaluators",
        icon: <IconCheckbox size={16} color="green" />,
        items: [
          {
            key: "simpleval",
            title: "Simple Evaluator",
            icon: nodeEmojis.simpleval,
            tooltip:
              "Evaluate responses with a simple check (no coding required).",
            onClick: () => addNode("simpleEval", "simpleval"),
          },
          {
            key: "evaluator-javascript",
            title: "JavaScript Evaluator",
            icon: nodeEmojis.evaluator,
            tooltip: "Evaluate responses by writing JavaScript code.",
            onClick: () =>
              addNode("evalNode", "evaluator", {
                language: "javascript",
                code: "function evaluate(response) {\n  return response.text.length;\n}",
              }),
          },
          {
            key: "evaluator-python",
            title: "Python Evaluator",
            icon: nodeEmojis.evaluator,
            tooltip: "Evaluate responses by writing Python code.",
            onClick: () =>
              addNode("evalNode", "evaluator", {
                language: "python",
                code: "def evaluate(response):\n  return len(response.text)",
              }),
          },
          {
            key: "llmeval",
            title: "LLM Evaluation",
            icon: nodeEmojis.llmeval,
            tooltip:
              "Evaluate responses with an LLM. (Note that LLM evaluators should be used with caution and always double-checked.)",
            onClick: () => addNode("llmeval"),
          },
          {
            key: "multieval",
            title: "Multi-Evaluator",
            icon: nodeEmojis.multieval,
            tooltip:
              "Evaluate responses across multiple criteria (multiple code and/or LLM evaluators).",
            onClick: () => addNode("multieval"),
          },
        ],
      },
      {
        key: "Processors",
        title: "Processors",
        icon: <IconTransform size={16} color="#f05f0c" />,
        items: [
          {
            key: "join",
            title: "Join Node",
            icon: nodeEmojis.join,
            tooltip:
              "Concatenate responses or input data together before passing into later nodes, within or across variables and LLMs.",
            onClick: () => addNode("join"),
          },
          {
            key: "split",
            title: "Split Node",
            icon: nodeEmojis.split,
            tooltip:
              "Split responses or input data by some format. For instance, you can split a markdown list into separate items.",
            onClick: () => addNode("split"),
          },
          {
            key: "processor-javascript",
            title: "JavaScript Processor",
            icon: nodeEmojis.evaluator,
            tooltip:
              "Transform responses by mapping a JavaScript function over them.",
            onClick: () =>
              addNode("process", "processor", {
                language: "javascript",
                code: "function process(response) {\n  return response.text;\n}",
              }),
          },
          {
            key: "processor-python",
            title: "Python Processor",
            icon: nodeEmojis.evaluator,
            tooltip:
              "Transform responses by mapping a Python function over them.",
            onClick: () =>
              addNode("process", "processor", {
                language: "python",
                code: "def process(response):\n  return response.text;",
              }),
          },
        ],
      },
      {
        key: "divider",
      },
      {
        // Menu.Label
        key: "Visualizers",
      },
      {
        key: "vis",
        title: "Vis Node",
        icon: nodeEmojis.vis,
        tooltip:
          "Plot evaluation results. (Attach an evaluator or scorer node as input.)",
        onClick: () => addNode("visNode", "vis", {}),
      },
      {
        key: "inspect",
        title: "Inspect Node",
        icon: nodeEmojis.inspect,
        tooltip:
          "Used to inspect responses from prompter or evaluation nodes, without opening up the pop-up view.",
        onClick: () => addNode("inspectNode", "inspect"),
      },
      {
        key: "divider",
      },
    ] as NestedMenuItemProps[];

    // Show the RAG group if anything in it is usable. ragNodes still holds
    // its label and divider when every node is filtered out, hence the >2.
    if (ragNodes.length > 2)
      initNodes = [...initNodes, ...ragNodes, ...miscNodes];
    else initNodes = [...initNodes, ...miscNodes];

    // Add favorite nodes to the menu
    const favoriteNodes = favorites?.nodes?.map(({ name, value, uid }) => {
      const type = value.type ?? "";
      const emoji =
        type in nodeEmojis ? nodeEmojis[type as keyof typeof nodeEmojis] : "❤️";
      return {
        key: uid,
        title: name,
        icon: emoji,
        tooltip: `Add ${name} to the flow`,
        onClick: () => addNodeFromFavorite(name, value),
        onTrash: (closeMenu) => {
          removeFavorite("nodes", uid);
          closeMenu();
        },
      } as NestedMenuItemProps;
    });

    if (favoriteNodes && favoriteNodes.length > 0) {
      initNodes.splice(0, 0, {
        key: "Favorites",
        title: "Favorites",
        icon: <IconHeart size={16} color="red" />,
        items: favoriteNodes,
      });
      initNodes.splice(1, 0, {
        key: "divider",
      });
    }

    return initNodes;
  }, [favorites, addNode]);

  // Add a node from a user's saved favorite
  const addNodeFromFavorite = useCallback(
    (name: string, value: Node) => {
      const data = { ...value.data };
      if (data.title === undefined) data.title = name;
      addNode(value.type ?? "favorite", value.type, data);
    },
    [addNode],
  );

  const onClickExamples = () => {
    if (examplesModal && examplesModal.current) examplesModal.current.trigger();
  };
  const onClickSettings = () => {
    if (settingsModal && settingsModal.current) settingsModal.current.trigger();
  };

  const handleError = useCallback(
    (err: Error | string) => {
      const msg = typeof err === "string" ? err : err.message;
      setIsLoading(false);
      setWaitingForShare(false);
      if (showAlert) showAlert(msg);
      console.error(msg);
    },
    [showAlert],
  );

  /**
   * SAVING / LOADING, IMPORT / EXPORT (from JSON)
   */
  const downloadJSON = (jsonData: JSONCompatible, filename: string) => {
    // Convert JSON object to JSON string
    const jsonString = JSON.stringify(jsonData, null, 2);

    // Create a Blob object from the JSON string
    const blob = new Blob([jsonString], { type: "application/json" });

    // Create a temporary download link
    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = filename;

    // Add the link to the DOM (it's not visible)
    document.body.appendChild(downloadLink);

    // Trigger the download by programmatically clicking the temporary link
    downloadLink.click();

    // Remove the temporary link from the DOM and revoke the URL
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadLink.href);
  };

  const exportYml = useCallback(
    async (flowData?: unknown) => {
      if (!rfInstance && !flowData) return;
      // We first get the data of the flow, if we haven't already
      const flow = flowData ?? rfInstance?.toObject();
      if (!flow) return;
      await jsontoYml(JSON.stringify(flow), flowFileName, 0, 1, handleError);
    },
    [rfInstance, flowFileName],
  );

  // Export flow to JSON
  const exportFlow = useCallback(
    (
      flowData?: unknown,
      saveToLocalFilesystem?: string,
      hideErrorAlert?: boolean,
      onError?: () => void,
    ) => {
      if (!rfInstance && !flowData) return;

      // We first get the data of the flow, if we haven't already
      const flow = flowData ?? rfInstance?.toObject();

      // Then we grab all the relevant cache files from the backend
      const all_node_ids = nodes.map((n) => n.id);
      return exportCache(all_node_ids)
        .then(function (cacheData) {
          // Now we append the cache file data to the flow
          const flow_and_cache = {
            flow,
            cache: cacheData,
          };

          // Save!
          const name = `${saveToLocalFilesystem ?? flowFileName}`;
          const flowFile = `${name}.cforge`;
          if (saveToLocalFilesystem !== undefined)
            // No need to export media files if we're saving locally
            return saveFlowToLocalFilesystem(
              flow_and_cache,
              flowFile,
              saveToLocalFilesystem !== "__autosave",
            );
          else if (MediaLookup.hasAnyMedia() && IS_RUNNING_LOCALLY)
            // There are media files and we're running locally,
            // so we need to export a self-contained .cfzip bundle instead
            exportFlowBundle(flow_and_cache, name);
          // No media files or we're on the browser, so
          // we can export a .cforge file (JSON). Media files
          // should be included in the cache data under __media.
          else downloadJSON(flow_and_cache as any, flowFile);
        })
        .catch((err) => {
          if (onError) onError();
          if (hideErrorAlert) console.error(err);
          else handleError(err);
        });
    },
    [rfInstance, nodes, flowFileName, handleError],
  );

  // Save the current flow to localStorage for later recall. Useful to getting
  // back progress upon leaving the site / browser crash / system restart.
  const saveFlow = useCallback(
    (
      rf_inst?: ReactFlowInstance,
      fileName?: string,
      hideErrorAlert?: boolean,
    ) => {
      const rf = rf_inst ?? rfInstance;
      if (!rf) return;
      if (flowLoad.isLoading) {
        console.log("Not saving: a flow is still loading.");
        return;
      }

      setShowSaveSuccess(false);

      startSaveTransition(() => {
        // Get current flow state
        const flow = rf.toObject();
        const loadGeneration = flowLoad.generation;

        const saveToLocalStorage = () => {
          // Running locally, this is the fallback for a failed save to the
          // filesystem, so it runs later. If a flow was loaded meanwhile,
          // `flow` is the previous one: don't write it over the loaded flow.
          if (flowLoad.generation !== loadGeneration) return true;

          // This line only saves the front-end state. Cache files
          // are not pulled or overwritten upon loading from localStorage.
          const flowSaved = StorageCache.saveToLocalStorage(
            "chainforge-flow",
            flow,
          );

          // Attempt to save the current back-end state,
          // in the StorageCache. (This does LZ compression to save space.)
          const stateSaved =
            StorageCache.saveToLocalStorage("chainforge-state");

          // Both must land for the flow to be recoverable. The usual cause of
          // failure is the browser's localStorage quota (~5MB in most
          // browsers), which large flows and uploaded documents can exhaust.
          return flowSaved && stateSaved;
        };

        const onFlowSaved = () => {
          console.log("Flow saved!");
          setShowSaveSuccess(true);
          setTimeout(() => {
            setShowSaveSuccess(false);
          }, 1000);
        };

        // If running locally, aattempt to save a copy of the flow to the lcoal filesystem,
        // so it shows up in the list of saved flows.
        if (IS_RUNNING_LOCALLY) {
          // SAVE TO LOCAL FILESYSTEM (only), and if that fails, try to save to localStorage
          exportFlow(
            flow,
            fileName ?? flowFileName,
            hideErrorAlert,
            saveToLocalStorage,
          )?.then(onFlowSaved);
        } else {
          // SAVE TO BROWSER LOCALSTORAGE
          // NOTE: Do not report success unconditionally here. saveToLocalStorage
          // returns false when the browser refuses the write (quota exceeded),
          // and claiming "Flow saved!" in that case loses the user's work
          // silently.
          if (saveToLocalStorage()) {
            setSaveFailed(false);
            onFlowSaved();
            // A save the user asked for (autosave hides alerts): ask the
            // browser not to clear its storage. Not on autosave, since
            // Firefox asks the user, which should follow their own action.
            if (!hideErrorAlert) requestStorageProtection();
          } else {
            const msg =
              "Could not save this flow to browser storage: the browser's " +
              "storage limit was reached. Export the flow to a file to avoid " +
              "losing work, and consider removing large uploaded documents " +
              "or running ChainForge locally.";
            // Autosave passes hideErrorAlert, so don't pop an alert every
            // minute -- but do leave the failure visible on the save button.
            setSaveFailed(true);
            if (hideErrorAlert) console.error(msg);
            else handleError(msg);
          }
        }
      });
    },
    [rfInstance, exportFlow, flowFileName, handleError],
  );

  // Keyboard save handler
  const handleCtrlSave = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        // User has pressed Ctrl+S. Save the current state:
        saveFlow();
      }
    },
    [saveFlow],
  );

  // The autosave interval outlives the render that started it, so it calls the
  // latest saveFlow through this ref. Calling saveFlow directly kept the state
  // from when autosaving started, e.g. before a loaded flow's nodes arrived, so
  // every autosave exported no responses, and they were lost on the next reload.
  const saveFlowRef = useRef(saveFlow);
  useEffect(() => {
    saveFlowRef.current = saveFlow;
  }, [saveFlow]);

  // Initialize auto-saving
  const initAutosaving = useCallback(
    (rf_inst: ReactFlowInstance, reinit?: boolean) => {
      if (autosavingInterval !== undefined) {
        // Autosaving interval already set
        if (reinit)
          clearInterval(autosavingInterval); // reinitialize interval, clearing the current one
        else return; // do nothing
      }
      console.log("Init autosaving");

      // Autosave the flow to localStorage every minute:
      const interv = setInterval(() => {
        // Check the visibility of the browser tab --if it's not visible, don't autosave
        if (!browserTabIsActive()) return;

        // Start a timer, in case the saving takes a long time
        const startTime = Date.now();

        // Save the flow to localStorage, and (if running locally) a copy to the filesystem
        saveFlowRef.current(rf_inst, "__autosave", true); // surpress error alerts when autosaving

        // Check how long the save took
        const duration = Date.now() - startTime;
        if (duration > 1500) {
          // If the operation took longer than 1.5 seconds, that's not good.
          // Although this function is called async inside setInterval,
          // calls to localStorage block the UI in JavaScript, freezing the screen.
          // We smart-disable autosaving here when we detect it's starting the freeze the UI:
          console.warn(
            "Autosaving disabled. The time required to save to localStorage exceeds 1 second. This can happen when there's a lot of data in your flow. Make sure to export frequently to save your work.",
          );
          clearInterval(interv);
          setAutosavingInterval(undefined);
        }
      }, 60000); // 60000 milliseconds = 1 minute
      setAutosavingInterval(interv);
    },
    [autosavingInterval, saveFlow, flowFileName],
  );

  // Triggered when user confirms 'New Flow' button
  const resetFlow = useCallback(
    (name?: string | null) => {
      resetLLMColors();

      const uid = (id: string) => `${id}-${Date.now()}`;
      const starting_nodes = [
        {
          id: uid("prompt"),
          type: "prompt",
          data: {
            prompt: "",
            n: 1,
            llms: [INITIAL_LLM()],
          },
          position: { x: 450, y: 200 },
        },
        {
          id: uid("textfields"),
          type: "textfields",
          data: {},
          position: { x: 80, y: 270 },
        },
      ];

      setNodes(starting_nodes);
      setEdges([]);

      StorageCache.clear();
      MediaLookup.clear();

      // New flow filename
      if (name == null) name = `flow-${Date.now()}`;
      setFlowFileNameAndCache(name);

      if (rfInstance) rfInstance.setViewport({ x: 200, y: 80, zoom: 1 });
    },
    [setNodes, setEdges, resetLLMColors, rfInstance],
  );

  const loadFlow = useCallback(
    async (flow?: Dict, rf_inst?: ReactFlowInstance | null) => {
      if (flow == null) {
        flowLoad.cancel();
        return;
      }
      // No saving until these nodes are on the canvas: until then, it's empty.
      const loadedNodes: Node[] = flow.nodes || [];
      flowLoad.awaitNodes(loadedNodes);
      if (rf_inst) {
        if (flow.viewport)
          rf_inst.setViewport({
            x: flow.viewport.x || 0,
            y: flow.viewport.y || 0,
            zoom: flow.viewport.zoom || 1,
          });
        else rf_inst.setViewport({ x: 0, y: 0, zoom: 1 });
      }
      resetLLMColors();

      // First, clear the ReactFlow state entirely
      // NOTE: We need to do this so it forgets any node/edge ids, which might have cross-over in the loaded flow.
      setNodes([]);
      setEdges([]);

      // After a delay, load in the new state.
      setTimeout(() => {
        setNodes(loadedNodes);
        setEdges(flow.edges || []);

        // Save flow that user loaded to autosave cache, in case they refresh the browser
        StorageCache.saveToLocalStorage("chainforge-flow", flow);

        // Cancel loading spinner
        setIsLoading(false);
      }, 10);

      // Start auto-saving, if it's not already enabled
      if (rf_inst) initAutosaving(rf_inst, true);
    },
    [resetLLMColors, setNodes, setEdges, initAutosaving],
  );

  const importGlobalStateFromCache = useCallback(() => {
    importState(StorageCache.getAllMatching((key) => key.startsWith("r.")));
  }, [importState]);

  // Find the autosaved flow, if it exists, returning
  // whether it exists and the location ("browser" or "filesystem") that it exists at.
  const autosavedFlowExists = useCallback(async () => {
    if (IS_RUNNING_LOCALLY) {
      // If running locally, we try to fetch a flow autosaved on the user's local machine first:
      try {
        const response = await axios.get(
          `${FLASK_BASE_URL}api/flowExists/__autosave`,
        );
        const autosave_file_exists = response.data.exists as boolean;
        if (autosave_file_exists)
          return { exists: autosave_file_exists, location: "filesystem" };
      } catch (error) {
        // Soft fail, continuing onwards to checking localStorage instead
      }
    }

    return {
      exists: window.localStorage.getItem("chainforge-flow") !== null,
      location: "browser",
    };
  }, []);

  // Import data to the cache stored on the local filesystem (in backend)
  const handleImportCache = useCallback(
    (cache_data: Dict<Dict>, replaceMedia: boolean) =>
      importCache(cache_data, { replaceMedia })
        .then(importGlobalStateFromCache)
        .catch(handleError),
    [handleError, importGlobalStateFromCache],
  );

  // `source` decides whether the previous flow's stored media are cleared;
  // see flowLoadReplacesMedia.
  const importFlowFromJSON = useCallback(
    (
      flowJSON: Dict,
      source: FlowLoadSource,
      rf_inst?: ReactFlowInstance | null,
    ) => {
      const rf = rf_inst ?? rfInstance;
      const replaceMedia = flowLoadReplacesMedia(source);

      setIsLoading(true);
      // Saving now would store the current flow alongside the incoming cache.
      // loadFlow takes over once the flow's nodes are known.
      flowLoad.begin();
      const importGeneration = flowLoad.generation;

      // Delay briefly, to ensure there's time for
      // the isLoading spinner to appear:
      setTimeout(() => {
        try {
          // Detect if there's no cache data
          if (!flowJSON.cache) {
            // Support for loading old flows w/o cache data:
            loadFlow(flowJSON, rf);
            StringLookup.restoreFrom([]); // manually clear the string lookup table
            // Not for a bundle, whose media were just imported, or the autosave.
            if (replaceMedia) MediaLookup.clear();
            return;
          }

          // Then we need to extract the JSON of the flow vs the cache data
          const flow = flowJSON.flow;
          const cache = flowJSON.cache;

          // We need to send the cache data to the backend first,
          // before we can load the flow itself...
          handleImportCache(cache, replaceMedia)
            .then(() => {
              // We load the ReactFlow instance last
              loadFlow(flow, rf);
            })
            .catch((err) => {
              // On an error, still try to load the flow itself:
              handleError(
                "Error encountered when importing cache data:" +
                  err.message +
                  "\n\nTrying to load flow regardless...",
              );
              loadFlow(flow, rf);
            });
        } catch (err) {
          // E.g. flowJSON was null. The canvas still holds the previous flow;
          // unless loadFlow already took over, end the load so saving resumes.
          if (flowLoad.generation === importGeneration) flowLoad.cancel();
          setIsLoading(false);
          handleError(err as Error);
        }
      }, 100);
    },
    [rfInstance, handleImportCache, loadFlow, handleError],
  );

  // Import a ChainForge flow from a file
  const importFlowFromFile = useCallback(async () => {
    // Create an input element with type "file" and accept only JSON files
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".cforge, .json, .cfzip, .zip";

    // Handle file selection
    input.addEventListener(
      "change",
      // @ts-expect-error The event is correctly typed here, but for some reason TS doesn't pick up on it.
      function (event: React.ChangeEvent<HTMLInputElement>) {
        // Start loading spinner
        setIsLoading(false);

        const files = event.target.files;
        if (!files || typeof files !== "object" || files.length === 0) {
          console.error("No files found to load.");
          return;
        }

        // We only support one file at a time
        const file = files[0];

        if (file.name.endsWith(".cfzip") || file.name.endsWith(".zip")) {
          // Read a ChainForge bundled zip file
          importFlowBundle(file)
            .then(({ flow, flowName }) => {
              // NOTE: At this point, any media files have either been
              // imported to the backend, or are in the MediaLookup cache.

              // Import the flow JSON data to the front-end
              importFlowFromJSON(flow, "bundle");

              // Set the name to the filename, for consistent saving
              safeSetFlowFileName(flowName);
            })
            .catch((err) => {
              handleError(
                "Critical error encountered when importing ChainForge flow bundle:" +
                  err.message,
              );
            });
        } else {
          // Read a .cforge JSON file
          const reader = new window.FileReader();
          const fileName = file.name
            ?.replace(".cforge", "")
            .replace(".json", "");

          // Handle file load event
          reader.addEventListener("load", function () {
            try {
              if (typeof reader.result !== "string")
                throw new Error(
                  "File could not be read: Unknown format or empty.",
                );

              // We try to parse the JSON response
              const flow_and_cache = JSON.parse(reader.result);

              // Import it to React Flow and import cache data on the backend
              importFlowFromJSON(flow_and_cache, "file");

              // Set the name to the filename, for consistent saving
              safeSetFlowFileName(fileName);
            } catch (error) {
              handleError(error as Error);
            }
          });

          // Read the selected file as text
          reader.readAsText(file);
        }
      },
    );

    // Trigger the file selector
    input.click();
  }, [importFlowFromJSON, handleError, safeSetFlowFileName]);

  const loadFlowFromAutosave = useCallback(
    async (rf_inst: ReactFlowInstance, fromFilesystem?: boolean) => {
      if (fromFilesystem) {
        // From local filesystem
        let response;
        try {
          // Fetch the flow
          response = await axios.get(`${FLASK_BASE_URL}api/flows/__autosave`);
        } catch (error) {
          console.error(
            "Error encountered when loading autosave from local filesystem:",
            error,
          );

          // Clear out the flow name, if set, so that it doesn't overwrite
          setFlowFileNameAndCache(`flow-${Date.now()}`);
          // Soft fail
          return;
        }

        // Attempt to load flow into the UI
        try {
          importFlowFromJSON(response.data, "autosave", rf_inst);
          console.log("Loaded flow from autosave on local machine.");
        } catch (error) {
          handleError(error as Error);
        }
      } else {
        // From browser localStorage
        const saved_flow = StorageCache.loadFromLocalStorage(
          "chainforge-flow",
          false,
        ) as Dict;
        if (saved_flow) {
          StorageCache.loadFromLocalStorage("chainforge-state", true);
          importGlobalStateFromCache();
          loadFlow(saved_flow, rf_inst);
        }
      }
    },
    [
      importGlobalStateFromCache,
      loadFlow,
      importFlowFromJSON,
      handleError,
      setFlowFileNameAndCache,
    ],
  );

  // cfzip importer
  const importFlowZipFromURL = useCallback(
    async (url: string) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
      const blob = await res.blob();

      // ensure filename ends with .cfzip
      const urlName =
        new URL(url, window.location.origin).pathname.split("/").pop() ||
        "example.cfzip";
      const fileName = /\.cfzip$/i.test(urlName) ? urlName : `${urlName}.cfzip`;

      const file = new File([blob], fileName, { type: "application/zip" });

      const { flow, flowName } = await importFlowBundle(file);
      importFlowFromJSON(flow, "bundle");
      await safeSetFlowFileName(flowName);
    },
    [importFlowFromJSON, safeSetFlowFileName],
  );

  // loader for example flows
  const onSelectExampleFlow = async (name: string, category?: string) => {
    setIsLoading(true);
    try {
      // OpenAI evals are served from their own directory, not examples/.
      // The modal tells us which tab the card came from.
      if (category === "openai-eval") {
        const flowJSON = await fetchOpenAIEval(name.replace(/\.cforge$/i, ""));
        importFlowFromJSON(withCurrentOpenAIEvalModel(flowJSON), "openai-eval");
        setFlowFileNameAndCache(`flow-${Date.now()}`);
        return;
      }

      // Hosted on a site, the flows sit next to the app itself: PUBLIC_URL is
      // the path the build was made for ("/play"), and is "" for a local
      // build. FLASK_BASE_URL is http://localhost:8000/ whenever the app is
      // NOT running locally, so using it here made the web build fetch the
      // visitor's own machine, which fails with a bare "Load failed".
      const base = IS_RUNNING_LOCALLY
        ? FLASK_BASE_URL.replace(/\/$/, "")
        : process.env.PUBLIC_URL ?? "";

      if (/\.cfzip$/i.test(name)) {
        const file = name.endsWith(".cfzip") ? name : `${name}.cfzip`;
        const url = name.startsWith("http") ? name : `${base}/examples/${file}`;
        await importFlowZipFromURL(url);
        return;
      }

      // treat everything else as .cforge JSON
      const baseName = name.replace(/\.cforge$/i, "");
      const flowJSON = await fetchExampleFlow(baseName);
      importFlowFromJSON(flowJSON, "example");
      setFlowFileNameAndCache(`flow-${Date.now()}`);
    } catch (err) {
      handleError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  // When the user clicks the 'New Flow' button
  const [requestFlowNameOpened, setRequestFlowNameOpened] = useState(false);
  const [requestFlowNameProps, setRequestFlowNameProps] = useState<
    Partial<RequestClarificationModalProps>
  >({});
  const requestClarificationModal = useMemo(
    () => (
      <RequestClarificationModal
        opened={requestFlowNameOpened}
        title={requestFlowNameProps.title ?? ""}
        question={requestFlowNameProps.question ?? ""}
        desc={requestFlowNameProps.desc}
        onSubmit={requestFlowNameProps.onSubmit ?? (() => {})}
        validator={(answer) => {
          // Ensure the filename contains only valid characters
          const invalidChars = /[<>:"/\\|?*\x00-\x1F.]/g;
          if (!answer || invalidChars.test(answer)) {
            return "Flow name contains invalid characters.";
          } else if (answer && answer.length >= 255) {
            return "Flow name too long. Must be less than 255 characters.";
          }
          return null;
        }}
      />
    ),
    [requestFlowNameProps, requestFlowNameOpened],
  );
  const onClickNewFlow = useCallback(() => {
    setRequestFlowNameProps({
      title: "Create a new flow",
      question: "What do you want to name your new flow?",
      desc: "Any unsaved changes to your existing flow will be lost.",
      onSubmit: (answer) => {
        if (answer == null) {
          // User canceled; do nothing
          setRequestFlowNameOpened(false);
          return;
        }
        setRequestFlowNameOpened(false);
        resetFlow(answer); // Set the callback if user confirms action
      },
    });
    setRequestFlowNameOpened(true);
  }, [resetFlow]);

  // When the user clicks the 'Share Flow' button
  const onClickShareFlow = useCallback(async () => {
    if (IS_RUNNING_LOCALLY) {
      handleError(
        new Error(
          "Cannot upload flow to server database when running locally: Feature only exists on hosted version of ChainForge.",
        ),
      );
      return;
    } else if (waitingForShare === true) {
      handleError(
        new Error(
          "A share request is already in progress. Wait until the current share finishes before clicking again.",
        ),
      );
      return;
    }

    // Helper function
    function isFileSizeLessThan5MB(json_str: string) {
      const encoder = new TextEncoder();
      const encodedString = encoder.encode(json_str);
      const fileSizeInBytes = encodedString.length;
      const fileSizeInMB = fileSizeInBytes / (1024 * 1024); // Convert bytes to megabytes
      return fileSizeInMB < 5;
    }

    setWaitingForShare(true);

    // Package up the current flow:
    const flow = rfInstance?.toObject();
    const all_node_ids = nodes.map((n) => n.id);
    const cforge_data = await exportCache(all_node_ids)
      .then(function (cacheData) {
        // Now we append the cache file data to the flow
        return {
          flow,
          cache: cacheData,
        };
      })
      .catch(handleError);

    if (!cforge_data) return;

    // Compress the data and check it's compressed size < 5MB:
    const compressed = LZString.compressToUTF16(JSON.stringify(cforge_data));
    if (!isFileSizeLessThan5MB(compressed)) {
      handleError(
        new Error(
          "Flow filesize exceeds 5MB. You can only share flows up to 5MB or less. But, don't despair! You can still use 'Export Flow' to share your flow manually as a .cforge file.",
        ),
      );
      return;
    }

    // Try to upload the compressed cforge data to the server:
    fetch("/db/shareflow.php", {
      method: "POST",
      body: compressed,
    })
      .then((r) => r.text())
      .then((uid) => {
        if (!uid) {
          throw new Error("Received no response from server.");
        } else if (uid.startsWith("Error")) {
          // Error encountered during the query; alert the user
          // with the error message:
          throw new Error(uid);
        }

        // Share completed!
        setWaitingForShare(false);

        // The response should be a uid we can put in a GET request.
        // Generate the link:
        const base_url = new URL(
          window.location.origin + window.location.pathname,
        ); // the current address e.g., https://chainforge.ai/play
        const get_params = new URLSearchParams(base_url.search);
        // Add the 'f' parameter
        get_params.set("f", uid); // set f=uid
        // Update the URL with the modified search parameters
        base_url.search = get_params.toString();
        // Get the modified URL
        const get_url = base_url.toString();

        // Copies the GET URL to user's clipboard
        // and updates the 'Share This' button state:
        clipboard.copy(get_url);
      })
      .catch((err) => {
        handleError(err);
      });
  }, [
    rfInstance,
    nodes,
    IS_RUNNING_LOCALLY,
    handleError,
    clipboard,
    waitingForShare,
  ]);

  // Run once upon ReactFlow initialization
  const onInit = useCallback(
    (rf_inst: ReactFlowInstance) => {
      setRfInstance(rf_inst);

      if (IS_RUNNING_LOCALLY) {
        // If we're running locally, try to fetch API keys from Python os.environ variables in the locally running Flask backend:
        fetchEnvironAPIKeys()
          .then((api_keys) => {
            setAPIKeys(api_keys);
          })
          .catch((err) => {
            // Soft fail
            console.warn(
              "Warning: Could not fetch API key environment variables from Flask server. Error:",
              err.message,
            );
          });

        // We also need to fetch the current flowFileName
        // Attempt to get the last working filename on component mount
        const last_working_flow_filename = StorageCache.loadFromLocalStorage(
          "chainforge-cur-file",
        );
        if (
          last_working_flow_filename &&
          typeof last_working_flow_filename === "object" &&
          "flowFileName" in last_working_flow_filename
        ) {
          // Use last working flow name
          setFlowFileName(last_working_flow_filename.flowFileName as string);
        }
      } else {
        // Check if there's a shared flow UID in the URL as a GET param
        // If so, we need to look it up in the database and attempt to load it:
        const shared_flow_uid = getSharedFlowURLParam();
        if (shared_flow_uid !== undefined) {
          // A shared link can be opened by accident, so it must not silently
          // replace the user's work. If there is an autosaved flow, load it as
          // usual and ask before replacing it; nothing is overwritten (the
          // autosave included) unless they confirm. Cancelling leaves the
          // link in the URL, so they can export first and reload to open it.
          // With no autosave there is nothing to lose, so just open it.
          autosavedFlowExists().then(({ exists }) => {
            if (exists) loadFlowFromAutosave(rf_inst, false);
            fetchSharedFlow(shared_flow_uid)
              .then((cforge_json) => {
                if (exists) {
                  setPendingSharedFlow(cforge_json);
                  sharedFlowConfirmModal.current?.trigger();
                } else {
                  importFlowFromJSON(cforge_json, "shared-link", rf_inst);
                }
              })
              .catch(handleError);
          });

          setIsLoading(false);
          return;
        }
      }

      // Attempt to load an autosaved flow, if one exists:
      autosavedFlowExists().then(({ exists, location }) => {
        if (!exists) {
          // Load an interesting default starting flow for new users
          importFlowFromJSON(EXAMPLEFLOW_1, "starter", rf_inst);

          // Open a welcome pop-up
          // openWelcomeModal();
        } else if (location === "browser") {
          loadFlowFromAutosave(rf_inst, false);
        } else if (location === "filesystem") {
          loadFlowFromAutosave(rf_inst, true);
        }
      });

      // Turn off loading wheel
      setIsLoading(false);
    },
    [
      setAPIKeys,
      handleError,
      importFlowFromJSON,
      autosavedFlowExists,
      loadFlowFromAutosave,
    ],
  );

  useEffect(() => {
    // Cleanup the autosaving interval upon component unmount:
    return () => {
      clearInterval(autosavingInterval); // Clear the interval when the component is unmounted
    };
  }, []);

  // While this page is open, tell a server started with --idle-shutdown that
  // it is in use, so it keeps running. Ordinary servers don't ask for this.
  // See backend/serverHeartbeat.ts.
  useEffect(() => {
    if (!serverStopsWhenIdle()) return;
    return startServerHeartbeat({
      send: () => fetch(`${FLASK_BASE_URL}api/heartbeat`, { method: "POST" }),
    });
  }, []);

  const reactFlowUI = useMemo(() => {
    return (
      <div
        id="cf-root-container"
        style={{ display: "flex", height: "100vh" }}
        onPointerDown={hideContextMenu}
      >
        <div
          style={{
            height: "100%",
            backgroundColor: colorScheme === "light" ? "#eee" : "#222",
            flexGrow: "1",
          }}
        >
          <ReactFlow
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodes={nodes}
            edges={edges}
            // @ts-expect-error Node types won't perfectly fit unless we explicitly extend from RF's types; ignoring this for now.
            nodeTypes={nodeTypes}
            // @ts-expect-error Edge types won't perfectly fit unless we explicitly extend from RF's types; ignoring this for now.
            edgeTypes={edgeTypes}
            zoomOnPinch={false}
            zoomOnScroll={false}
            panOnScroll={true}
            disableKeyboardA11y={true}
            deleteKeyCode={[]}
            // connectionLineComponent={AnimatedConnectionLine}
            // connectionLineStyle={connectionLineStyle}
            snapToGrid={true}
            snapGrid={snapGrid}
            onInit={onInit}
            onError={() => {
              // Suppress ReactFlow warnings spamming the console.
              // console.log(err);
            }}
          >
            {/* Dots in a dull gray in dark mode, where #999 stands out. */}
            <Background
              color={colorScheme === "light" ? "#999" : "#444"}
              gap={16}
            />
            <Controls showZoom={true} />
          </ReactFlow>
        </div>
      </div>
    );
  }, [
    onNodesChange,
    onEdgesChange,
    onConnect,
    nodes,
    edges,
    onInit,
    hideContextMenu,
    colorScheme,
  ]);

  // Save when the tab is hidden or the page is unloaded. The minute-long
  // autosave interval skips hidden tabs, so work done shortly before switching
  // away -- e.g. adding images to a Media Node -- was lost if the browser then
  // closed or the page reloaded. (The uploaded files themselves are already in
  // IndexedDB; what was lost was the flow referencing them.) localStorage
  // writes are synchronous, so the browser-storage save completes even during
  // pagehide. Only armed once autosaving has started, and saveFlow refuses to
  // save while a flow is still loading, so a reload mid-load can't overwrite
  // the autosave with an empty flow.
  //
  // Only the tab the user last used saves this way: closing an old ChainForge
  // tab left open in the background would otherwise save its stale flow over
  // newer work from another tab. See ./backend/activeTab.
  const [tabId] = useState(() => uuid());
  useEffect(() => {
    if (autosavingInterval === undefined) return;
    const claim = () => claimActiveTab(tabId);
    const saveNow = () => {
      if (isActiveTab(tabId)) saveFlow(undefined, "__autosave", true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") saveNow();
      else claim();
    };
    // A tab loaded in the background hasn't been used yet, so doesn't claim.
    if (document.visibilityState === "visible") claim();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", saveNow);
    window.addEventListener("focus", claim);
    window.addEventListener("pointerdown", claim, true);
    window.addEventListener("keydown", claim, true);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", saveNow);
      window.removeEventListener("focus", claim);
      window.removeEventListener("pointerdown", claim, true);
      window.removeEventListener("keydown", claim, true);
    };
  }, [autosavingInterval, saveFlow, tabId]);

  // Recover the index of files persisted in IndexedDB by earlier sessions, so
  // the storage readout and export include them. Contents are loaded on demand
  // by MediaLookup.get, so this stays cheap.
  useEffect(() => {
    if (IS_RUNNING_LOCALLY) return; // files live on disk in that case
    MediaLookup.hydrateFromIndexedDB()
      .then((n) => {
        if (n > 0) console.log(`Recovered ${n} uploaded file(s) from storage.`);
      })
      .catch((err) => console.warn("Could not read persisted uploads:", err));
  }, []);

  // Whether the browser has agreed not to clear its storage, where flows and
  // files are kept when there's no local server. See ./backend/storagePersistence.
  const [storageProtection, setStorageProtection] =
    useState<StorageProtection>(getStorageProtection);
  const [storageNoticeClosed, setStorageNoticeClosed] = useState(
    storageNoticeDismissed,
  );
  useEffect(() => {
    if (IS_RUNNING_LOCALLY) return; // flows and files are saved to disk
    const unsubscribe = subscribeStorageProtection(setStorageProtection);
    // Reads the state without requesting it, so never prompts.
    checkStorageProtection();
    return unsubscribe;
  }, []);

  const storageRiskMessage = useMemo(() => {
    if (storageProtection.status !== "at-risk") return undefined;
    switch (storageProtection.reason) {
      case "safari":
        return (
          "Safari deletes a site's saved data after 7 days of use without " +
          "visiting it. Export this flow to keep a copy."
        );
      case "unsupported":
        return (
          "This browser may clear saved data to free up space. Export this " +
          "flow to keep a copy."
        );
      default:
        return (
          "The browser may clear saved data to free up space. Export this " +
          "flow to keep a copy."
        );
    }
  }, [storageProtection]);

  const saveMessage = useMemo(() => {
    if (isSaving) return "Saving...";
    else if (showSaveSuccess) return "Success!";
    else if (saveFailed)
      return (
        "Last save FAILED -- browser storage is full. Export this flow to a " +
        "file to avoid losing work."
      );
    else if (IS_RUNNING_LOCALLY) return "Save to local disk";
    else if (storageProtection.status === "protected")
      return "Save to this browser. The browser has agreed not to clear it.";
    else if (storageRiskMessage)
      return `Save to this browser. ${storageRiskMessage}`;
    else return "Save to local cache";
  }, [
    isSaving,
    showSaveSuccess,
    saveFailed,
    storageProtection,
    storageRiskMessage,
  ]);

  // Rename the current flow from the toolbar. Locally, the flow may already be
  // a file on disk, which must be renamed too -- otherwise the next save would
  // write a second file and leave the old one behind in the saved flows list.
  const renameCurrentFlow = useCallback(
    async (newName: string) => {
      if (!IS_RUNNING_LOCALLY) {
        // In the browser, the name is only used for exported filenames
        setFlowFileNameAndCache(newName);
        return true;
      }
      const flowExists = async (name: string) =>
        (
          await axios.get(
            `${FLASK_BASE_URL}api/flowExists/${encodeURIComponent(name)}`,
          )
        ).data.exists === true;
      try {
        if (await flowExists(flowFileName)) {
          // The backend refuses names that are already taken
          await axios.put(
            `${FLASK_BASE_URL}api/flows/${encodeURIComponent(flowFileName)}`,
            { newName },
          );
        } else if (await flowExists(newName)) {
          // Not saved yet, so there's nothing to rename on disk. But saving
          // under a taken name would silently overwrite that other flow.
          throw new Error("A flow with that name already exists.");
        }
        setFlowFileNameAndCache(newName);
        return true;
      } catch (err) {
        const msg = axios.isAxiosError(err)
          ? err.response?.data?.error ?? err.message
          : (err as Error).message;
        if (showAlert) showAlert(`Could not rename flow: ${msg}`);
        return false;
      }
    },
    [flowFileName, setFlowFileNameAndCache, showAlert],
  );

  const flowSidebar = useMemo(() => {
    if (!IS_RUNNING_LOCALLY) return undefined;
    return (
      <FlowSidebar
        currentFlow={flowFileName}
        onLoadFlow={(flowData, name) => {
          if (name !== undefined) {
            setFlowFileNameAndCache(name);
          }
          if (flowData !== undefined) {
            try {
              importFlowFromJSON(flowData, "saved-flow");
            } catch (error) {
              console.error(error);
              setIsLoading(false);
              if (showAlert) showAlert(error as Error);
            }
          }
        }}
      />
    );
  }, [flowFileName, importFlowFromJSON, showAlert, setFlowFileNameAndCache]);

  if (!IS_ACCEPTED_BROWSER) {
    return (
      <Box maw={600} mx="auto" mt="40px">
        <Text m="xl" size={"14px"}>
          {"We're sorry, but it seems like "}
          {isMobile
            ? "you are viewing ChainForge on a mobile device"
            : "your current browser isn't supported by the current version of ChainForge"}{" "}
          😔. We want to provide you with the best experience possible, so we
          recommend {isMobile ? "viewing ChainForge on a desktop browser" : ""}{" "}
          using one of our supported browsers listed below:
        </Text>
        <List m="xl" size={"14px"}>
          <List.Item>Google Chrome</List.Item>
          <List.Item>Mozilla Firefox</List.Item>
          <List.Item>Microsoft Edge (Chromium)</List.Item>
          <List.Item>Brave</List.Item>
          <List.Item>Safari</List.Item>
        </List>

        <Text m="xl" size={"14px"}>
          These browsers offer enhanced compatibility with ChainForge&apos;s
          features. Don&apos;t worry, though! We&apos;re working to expand our
          browser support to ensure everyone can enjoy our platform. 😊
        </Text>
        <Text m="xl" size={"14px"}>
          If you have any questions or need assistance, please don&apos;t
          hesitate to reach out on our{" "}
          <a href="https://github.com/ianarawjo/ChainForge/issues">GitHub</a> by{" "}
          <a href="https://github.com/ianarawjo/ChainForge/issues">
            opening an Issue.
          </a>
          &nbsp; (If you&apos;re a web developer, consider forking our
          repository and making a{" "}
          <a href="https://github.com/ianarawjo/ChainForge/pulls">
            Pull Request
          </a>{" "}
          to support your particular browser.)
        </Text>
      </Box>
    );
  } else
    return (
      <div onKeyDown={handleCtrlSave}>
        <GlobalSettingsModal ref={settingsModal} />
        <LoadingOverlay visible={isLoading} overlayBlur={1} />
        {requestClarificationModal}
        <AreYouSureModal
          ref={sharedFlowConfirmModal}
          title="Open shared flow?"
          message={
            "This link opens a flow someone shared. Opening it will replace the flow you're " +
            "working on. To keep your current work, click Cancel, export your flow to a file " +
            "(Export), then reload this page to open the shared flow."
          }
          onConfirm={() => {
            if (pendingSharedFlow)
              importFlowFromJSON(pendingSharedFlow, "shared-link");
            setPendingSharedFlow(null);
          }}
        />
        <ExampleFlowsModal
          ref={examplesModal}
          handleOnSelect={onSelectExampleFlow}
        />
        {flowSidebar}

        {/* <Modal title={'Welcome to ChainForge'} size='400px' opened={welcomeModalOpened} onClose={closeWelcomeModal} yOffset={'6vh'} styles={{header: {backgroundColor: '#FFD700'}, root: {position: 'relative', left: '-80px'}}}>
        <Box m='lg' mt='xl'>
          <Text>To get started, click the Settings icon in the top-right corner.</Text>
        </Box>
      </Modal> */}

        {reactFlowUI}

        {/* Top bar: left toolbar, flow name, right toolbar. It ignores clicks
            so the canvas beneath its empty space stays usable. */}
        <div
          style={{
            position: "fixed",
            left: "10px",
            right: "10px",
            top: "10px",
            zIndex: 8,
            display: "grid",
            // The side columns never shrink below their toolbars and split the
            // leftover space evenly. So the name sits at the window's center
            // when it fits there, and otherwise as close to it as it can get.
            gridTemplateColumns:
              "minmax(max-content, 1fr) minmax(0, max-content) minmax(max-content, 1fr)",
            columnGap: "16px",
            alignItems: "start",
            pointerEvents: "none",
          }}
        >
          <Flex
            id="custom-controls"
            align="center"
            // Leaves room for the saved flows sidebar toggle
            ml={IS_RUNNING_LOCALLY ? "34px" : 0}
            style={{ justifySelf: "start", pointerEvents: "auto" }}
          >
            <NestedMenu
              items={addNodesMenuItems}
              button={(toggleMenu) => (
                <Button
                  size="sm"
                  variant={colorScheme === "light" ? "gradient" : "filled"}
                  color={colorScheme === "light" ? "blue" : "gray"}
                  compact
                  mr="sm"
                  onClick={toggleMenu}
                >
                  Add Node +
                </Button>
              )}
            />
            {/* Export is a split button: the common case stays one click,
                while YAML -- which most people never use -- moves into the
                caret instead of occupying a second slot in the toolbar. */}
            <Button.Group mr="xs">
              <Button
                onClick={() => exportFlow()}
                size="sm"
                variant="outline"
                color={colorScheme === "light" ? "blue" : "gray"}
                bg={colorScheme === "light" ? "#eee" : "#222"}
                compact
              >
                Export
              </Button>
              <Menu shadow="md" width={210} position="bottom-end">
                <Menu.Target>
                  <Button
                    size="sm"
                    variant="outline"
                    color={colorScheme === "light" ? "blue" : "gray"}
                    bg={colorScheme === "light" ? "#eee" : "#222"}
                    compact
                    px={4}
                    title="More export options"
                    aria-label="More export options"
                  >
                    <IconChevronDown size={14} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconFileDownload size={14} />}
                    onClick={() => exportFlow()}
                  >
                    Export flow (.cforge)
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconFileCode size={14} />}
                    onClick={() => exportYml()}
                  >
                    Export as YAML (.yml)
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Button.Group>
            <Button
              onClick={importFlowFromFile}
              size="sm"
              variant="outline"
              color={colorScheme === "light" ? "blue" : "gray"}
              bg={colorScheme === "light" ? "#eee" : "#222"}
              compact
            >
              Import
            </Button>
            <Tooltip label={saveMessage} withArrow multiline width={300}>
              <Button
                variant="outline"
                ml="sm"
                size="sm"
                compact
                onClick={() => saveFlow()}
                color={
                  saveFailed ? "red" : colorScheme === "light" ? "blue" : "gray"
                }
                bg={colorScheme === "light" ? "#eee" : "#222"}
                loading={isSaving}
                disabled={isLoading || isSaving}
                leftIcon={
                  <IconDeviceFloppy
                    fill={colorScheme === "light" ? "#dde" : "#222"}
                  />
                }
                styles={{
                  leftIcon: {
                    marginRight: "3px",
                  },
                }}
              >
                Save
              </Button>
            </Tooltip>
          </Flex>
          <Box maw="240px" miw={0} style={{ pointerEvents: "auto" }}>
            <FlowNameEditor
              name={flowFileName}
              onRename={renameCurrentFlow}
              disabled={isLoading}
            />
          </Box>
          <div style={{ justifySelf: "end", pointerEvents: "auto" }}>
            {IS_RUNNING_LOCALLY ? (
              <></>
            ) : (
              <Button
                onClick={onClickShareFlow}
                size="sm"
                variant="outline"
                compact
                color={
                  clipboard.copied
                    ? "teal"
                    : colorScheme === "light"
                      ? "blue"
                      : "gray"
                }
                mr="xs"
                style={{ float: "left" }}
              >
                {waitingForShare ? (
                  <Loader size="xs" mr="4px" />
                ) : (
                  <IconFileSymlink size="16px" />
                )}
                {clipboard.copied
                  ? "Link copied!"
                  : waitingForShare
                    ? "Sharing..."
                    : "Share"}
              </Button>
            )}
            <Button
              onClick={onClickNewFlow}
              size="sm"
              variant="outline"
              color={colorScheme === "light" ? "blue" : "gray"}
              bg={colorScheme === "light" ? "#eee" : "#222"}
              compact
              mr="xs"
              style={{ float: "left" }}
            >
              {" "}
              New Flow{" "}
            </Button>
            <Button
              onClick={onClickExamples}
              size="sm"
              variant="filled"
              color={colorScheme === "light" ? "blue" : "gray"}
              compact
              mr="xs"
              style={{ float: "left" }}
            >
              {" "}
              Example Flows{" "}
            </Button>
            <Button
              onClick={onClickSettings}
              size="sm"
              variant={colorScheme === "light" ? "gradient" : "filled"}
              color={colorScheme === "light" ? "blue" : "gray"}
              compact
              style={{ width: "32px", minWidth: "32px", padding: 0 }}
            >
              <IconSettings size={18} />
            </Button>
          </div>
        </div>
        {/* Once work is saved in the browser, if the browser may clear it.
            Shown until dismissed, once per browser. */}
        {storageRiskMessage &&
          storageProtection.requested &&
          !storageNoticeClosed && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Your browser may clear this flow"
              color="yellow"
              withCloseButton
              closeButtonLabel="Dismiss"
              onClose={() => {
                dismissStorageNotice();
                setStorageNoticeClosed(true);
              }}
              style={{
                position: "fixed",
                left: "10px",
                top: "46px",
                zIndex: 8,
                maxWidth: "360px",
              }}
              styles={{ title: { marginBottom: 2 } }}
            >
              <Text size="xs">
                {storageRiskMessage} Running ChainForge locally saves flows to
                disk instead.
              </Text>
              <Button
                size="xs"
                compact
                mt={6}
                variant="light"
                color="yellow"
                leftIcon={<IconFileDownload size={14} />}
                onClick={() => exportFlow()}
              >
                Export flow
              </Button>
            </Alert>
          )}
        <div
          style={{
            position: "fixed",
            right: "10px",
            bottom: "20px",
            zIndex: 8,
          }}
        >
          <a
            href="https://forms.gle/qhr7T2Fe8gYJF16fA"
            target="_blank"
            style={{ color: "#666", fontSize: "14px" }}
            rel="noreferrer"
          >
            Send us feedback
          </a>
        </div>
      </div>
    );
};

export default App;
